import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { ArrowLeft, Upload, AlertCircle, Check, FileSpreadsheet, Download, Info } from 'lucide-react';
import Papa from 'papaparse';
import { getHouses } from '../../services/houseService';
import { getDepartments } from '../../services/departmentService';
import { House, Department } from '../../types/models';

interface ImportRow {
  'First Name': string;
  'Last Name': string;
  'Tamil First Name'?: string;
  'Tamil Last Name'?: string;
  'Date of Birth': string;
  'Gender': string;
  'House': string;
  'House Color'?: string;
  'Department'?: string;
  'Reference Number'?: string;
  'Payment Status'?: string;
  'Payment Date'?: string;
  'Payment Notes'?: string;
  [key: string]: any;
}

interface ValidationError {
  row: number;
  field: string;
  message: string;
}

const ImportParticipants: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ImportRow[]>([]);
  const [houses, setHouses] = useState<House[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [mappedData, setMappedData] = useState<any[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [importStats, setImportStats] = useState({
    total: 0,
    success: 0,
    failed: 0,
    duplicates: 0
  });

  useEffect(() => {
    checkAuthorization();
  }, []);

  const checkAuthorization = async () => {
    try {
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select(`
          roles!inner(
            role_name
          )
        `)
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

      const hasAccess = userRoles?.some(ur => 
        ur.roles.role_name === 'admin' || 
        ur.roles.role_name === 'manager'
      ) ?? false;

      setIsAuthorized(hasAccess);
      if (!hasAccess) {
        navigate('/');
      } else {
        loadReferenceData();
      }
    } catch (error) {
      console.error('Error checking authorization:', error);
      setError('Failed to verify access permissions');
    }
  };

  const loadReferenceData = async () => {
    try {
      setLoading(true);
      const [housesData, departmentsData] = await Promise.all([
        getHouses(),
        getDepartments()
      ]);
      setHouses(housesData);
      setDepartments(departmentsData);
    } catch (error: any) {
      console.error('Error loading reference data:', error);
      setError(error.message || 'Failed to load reference data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setSuccess(null);
      setValidationErrors([]);
      setParsedData([]);
      setMappedData([]);
      setShowPreview(false);
    }
  };

  const parseCSV = () => {
    if (!file) {
      setError(t('participants.import.selectFile'));
      return;
    }

    setValidating(true);
    setError(null);
    setValidationErrors([]);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data as ImportRow[];
        setParsedData(data);
        validateData(data);
      },
      error: (error) => {
        setError(`Error parsing CSV file: ${error.message}`);
        setValidating(false);
      }
    });
  };

  const validateData = (data: ImportRow[]) => {
    const errors: ValidationError[] = [];
    const validRows: any[] = [];

    data.forEach((row, index) => {
      const rowNumber = index + 2; // +2 because of 0-indexing and header row
      const validationResult = validateRow(row, rowNumber);
      
      if (validationResult.errors.length > 0) {
        errors.push(...validationResult.errors);
      } else {
        validRows.push(validationResult.mappedRow);
      }
    });

    setValidationErrors(errors);
    setMappedData(validRows);
    setValidating(false);

    if (errors.length === 0 && validRows.length > 0) {
      setShowPreview(true);
    }
  };

  const validateRow = (row: ImportRow, rowNumber: number) => {
    const errors: ValidationError[] = [];
    
    // Required fields
    if (!row['First Name']) {
      errors.push({ row: rowNumber, field: 'First Name', message: 'First name is required' });
    }
    
    if (!row['Last Name']) {
      errors.push({ row: rowNumber, field: 'Last Name', message: 'Last name is required' });
    }
    
    if (!row['Date of Birth']) {
      errors.push({ row: rowNumber, field: 'Date of Birth', message: 'Date of birth is required' });
    } else {
      // Validate date format (YYYY-MM-DD)
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(row['Date of Birth'])) {
        errors.push({ 
          row: rowNumber, 
          field: 'Date of Birth', 
          message: 'Date of birth must be in YYYY-MM-DD format' 
        });
      }
    }
    
    if (!row['Gender']) {
      errors.push({ row: rowNumber, field: 'Gender', message: 'Gender is required' });
    } else {
      // Validate gender (M or F)
      if (row['Gender'] !== 'M' && row['Gender'] !== 'F' && 
          row['Gender'] !== 'Male' && row['Gender'] !== 'Female') {
        errors.push({ 
          row: rowNumber, 
          field: 'Gender', 
          message: 'Gender must be M, F, Male, or Female' 
        });
      }
    }
    
    if (!row['House']) {
      errors.push({ row: rowNumber, field: 'House', message: 'House is required' });
    } else {
      // Validate house exists
      const houseExists = houses.some(h => 
        h.house_name.toLowerCase() === row['House'].toLowerCase()
      );
      
      if (!houseExists) {
        errors.push({ 
          row: rowNumber, 
          field: 'House', 
          message: `House "${row['House']}" does not exist` 
        });
      }
    }
    
    // Validate department if provided
    if (row['Department']) {
      const departmentExists = departments.some(d => 
        d.name.toLowerCase() === row['Department']?.toLowerCase()
      );
      
      if (!departmentExists) {
        errors.push({ 
          row: rowNumber, 
          field: 'Department', 
          message: `Department "${row['Department']}" does not exist` 
        });
      }
    }
    
    // Validate payment status if provided
    if (row['Payment Status'] && 
        row['Payment Status'] !== 'not_paid' && 
        row['Payment Status'] !== 'paid' && 
        row['Payment Status'] !== 'school_paid') {
      errors.push({ 
        row: rowNumber, 
        field: 'Payment Status', 
        message: 'Payment status must be not_paid, paid, or school_paid' 
      });
    }
    
    // Map the row to the database schema
    const mappedRow = {
      first_name: row['First Name'],
      last_name: row['Last Name'],
      first_name_tamil: row['Tamil First Name'] || null,
      last_name_tamil: row['Tamil Last Name'] || null,
      date_of_birth: row['Date of Birth'],
      gender: row['Gender'] === 'Male' ? 'M' : row['Gender'] === 'Female' ? 'F' : row['Gender'],
      house_id: houses.find(h => h.house_name.toLowerCase() === row['House'].toLowerCase())?.house_id,
      department_id: row['Department'] 
        ? departments.find(d => d.name.toLowerCase() === row['Department']?.toLowerCase())?.department_id 
        : null,
      reference_number: row['Reference Number'] || null,
      payment_status: row['Payment Status'] || 'not_paid',
      payment_date: row['Payment Date'] || null,
      payment_notes: row['Payment Notes'] || null
    };
    
    return { errors, mappedRow };
  };

  const importParticipants = async () => {
    if (mappedData.length === 0) {
      setError(t('participants.import.noValidData'));
      return;
    }

    try {
      setImporting(true);
      setError(null);
      setSuccess(null);
      
      const stats = {
        total: mappedData.length,
        success: 0,
        failed: 0,
        duplicates: 0
      };
      
      // Import in batches of 10 to avoid timeouts
      const batchSize = 10;
      for (let i = 0; i < mappedData.length; i += batchSize) {
        const batch = mappedData.slice(i, i + batchSize);
        
        // Check for existing participants with the same name and date of birth
        for (const participant of batch) {
          const { data: existingParticipants, error: checkError } = await supabase
            .from('students')
            .select('student_id')
            .eq('first_name', participant.first_name)
            .eq('last_name', participant.last_name)
            .eq('date_of_birth', participant.date_of_birth);
          
          if (checkError) {
            console.error('Error checking for duplicates:', checkError);
            stats.failed++;
            continue;
          }
          
          if (existingParticipants && existingParticipants.length > 0) {
            stats.duplicates++;
            continue;
          }
          
          // Insert the participant
          const { error: insertError } = await supabase
            .from('students')
            .insert([participant]);
          
          if (insertError) {
            console.error('Error inserting participant:', insertError);
            stats.failed++;
          } else {
            stats.success++;
          }
        }
      }
      
      setImportStats(stats);
      setSuccess(`Import completed: ${stats.success} participants imported successfully, ${stats.duplicates} duplicates skipped, ${stats.failed} failed.`);
    } catch (error: any) {
      console.error('Error importing participants:', error);
      setError(error.message || 'Failed to import participants');
    } finally {
      setImporting(false);
    }
  };

  const downloadSampleCSV = () => {
    const sampleData = [
      {
        'First Name': 'John',
        'Last Name': 'Doe',
        'Tamil First Name': 'ஜான்',
        'Tamil Last Name': 'டோ',
        'Date of Birth': '2010-01-15',
        'Gender': 'Male',
        'House': 'Red House',
        'House Color': '#ff0000',
        'Department': 'Oslo',
        'Reference Number': '1001',
        'Payment Status': 'not_paid',
        'Payment Date': '',
        'Payment Notes': ''
      },
      {
        'First Name': 'Jane',
        'Last Name': 'Smith',
        'Tamil First Name': 'ஜேன்',
        'Tamil Last Name': 'ஸ்மித்',
        'Date of Birth': '2011-05-20',
        'Gender': 'Female',
        'House': 'Blue House',
        'House Color': '#0000ff',
        'Department': 'Bergen',
        'Reference Number': '1002',
        'Payment Status': 'paid',
        'Payment Date': '2023-05-10',
        'Payment Notes': 'Paid by parent'
      }
    ];
    
    const csv = Papa.unparse(sampleData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'sample_participants.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <button
          onClick={() => navigate(-1)}
          className="mr-4 inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">{t('participants.import.title')}</h1>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4">
          <div className="flex">
            <Check className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-700">{success}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">{t('participants.import.title')}</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            {t('participants.import.instructions')}
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
            <div className="flex">
              <Info className="h-5 w-5 text-blue-400" />
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  Required columns: First Name, Last Name, Date of Birth, Gender, House
                </p>
                <p className="text-sm text-blue-700 mt-1">
                  Optional columns: Tamil First Name, Tamil Last Name, Department, Reference Number, Payment Status, Payment Date, Payment Notes
                </p>
                <p className="text-sm text-blue-700 mt-2">
                  <strong>Note:</strong> Gender should be 'Male' or 'Female'. House and department names must match existing records.
                </p>
                <button
                  onClick={downloadSampleCSV}
                  className="mt-2 inline-flex items-center px-3 py-1.5 border border-blue-300 shadow-sm text-xs font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Download className="h-4 w-4 mr-1" />
                  {t('participants.import.downloadSample')}
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('participants.import.fileUpload')}
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <FileSpreadsheet className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                    >
                      <span>Upload a file</span>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        className="sr-only"
                        accept=".csv"
                        onChange={handleFileChange}
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    {t('participants.import.uploadHint')}
                  </p>
                </div>
              </div>
              {file && (
                <p className="mt-2 text-sm text-gray-500">
                  Selected file: {file.name}
                </p>
              )}
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={parseCSV}
                disabled={!file || validating}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
              >
                {validating ? t('participants.import.validating') : t('participants.import.validate')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {validationErrors.length > 0 && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">{t('participants.import.validationErrors')}</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              {t('participants.import.validationErrorsHint')}
            </p>
          </div>
          <div className="border-t border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('participants.import.row')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('participants.import.field')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('participants.import.error')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {validationErrors.map((error, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {error.row}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {error.field}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {error.message}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showPreview && mappedData.length > 0 && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">{t('participants.import.dataPreview')}</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              {t('participants.import.dataPreviewHint')} {mappedData.length} {t('participants.import.records')}
            </p>
          </div>
          <div className="border-t border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('participants.table.name')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('participants.table.tamilName')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('participants.table.dateOfBirth')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('participants.table.gender')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('participants.table.house')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('participants.table.department')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('participants.table.referenceNumber')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {mappedData.slice(0, 10).map((row, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {row.first_name} {row.last_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {row.first_name_tamil} {row.last_name_tamil}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {row.date_of_birth}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          row.gender === 'M' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'
                        }`}>
                          {row.gender === 'M' ? t('participants.filters.male') : t('participants.filters.female')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {houses.find(h => h.house_id === row.house_id)?.house_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {row.department_id ? departments.find(d => d.department_id === row.department_id)?.name : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {row.reference_number || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {mappedData.length > 10 && (
                <div className="px-6 py-3 bg-gray-50 text-sm text-gray-500">
                  {t('participants.import.showing')} 10 {t('participants.import.of')} {mappedData.length} {t('participants.import.records')}
                </div>
              )}
            </div>
            <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
              <button
                type="button"
                onClick={importParticipants}
                disabled={importing}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
              >
                <Upload className="h-4 w-4 mr-2" />
                {importing ? t('participants.import.importing') : t('participants.import.import')}
              </button>
            </div>
          </div>
        </div>
      )}

      {importStats.total > 0 && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">{t('participants.import.importResults')}</h3>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-gray-500">{t('participants.import.totalRecords')}</div>
                <div className="mt-1 text-3xl font-semibold text-gray-900">{importStats.total}</div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-green-700">{t('participants.import.successfullyImported')}</div>
                <div className="mt-1 text-3xl font-semibold text-green-900">{importStats.success}</div>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-yellow-700">{t('participants.import.duplicatesSkipped')}</div>
                <div className="mt-1 text-3xl font-semibold text-yellow-900">{importStats.duplicates}</div>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-red-700">{t('participants.import.failed')}</div>
                <div className="mt-1 text-3xl font-semibold text-red-900">{importStats.failed}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportParticipants;